import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp, useT } from "@/context/AppContext";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";

export function BundlesPage() {
  const t = useT();
  const { lang, formatPrice } = useApp();
  const [bundles, setBundles] = useState([]);
  const nav = useNavigate();

  useEffect(() => { api.get("/bundles").then((r) => setBundles(r.data)); }, []);

  return (
    <div className="container-x py-12" data-testid="bundles-page">
      <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-3">Pakketten</div>
      <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">Aanbevolen bundels</h1>
      <p className="text-[#A8B3AD] mt-2">Kant-en-klare combinaties van hardware + software + add-ons.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
        {bundles.map((b, i) => (
          <motion.div key={b.id}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="card-interactive overflow-hidden" onClick={() => nav(`/bundles/${b.id}`)}
            data-testid={`bundle-card-${b.slug}`}>
            {b.image_url && (
              <div className="bg-gradient-to-br from-[#1C2724] to-[#131B18] h-52 grid place-items-center p-4">
                <img src={b.image_url} alt={tr(b.name_i18n, lang)} className="max-h-full max-w-full object-contain" />
              </div>
            )}
            <div className="p-6">
              <div className="chip chip-accent">{b.discount_pct}% voordeel</div>
              <h3 className="display text-xl font-semibold mt-3 text-[#F4F1EA]">{tr(b.name_i18n, lang)}</h3>
              <p className="text-sm text-[#A8B3AD] mt-2">{tr(b.description_i18n, lang)}</p>
              <div className="mt-4 text-[#6FAA8A] font-semibold flex items-center gap-2">
                Bekijk <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function BundleDetailPage() {
  const { id } = useParams();
  const { lang, formatPrice, addToCart } = useApp();
  const nav = useNavigate();
  const [b, setB] = useState(null);

  useEffect(() => { api.get(`/bundles/${id}`).then((r) => setB(r.data)); }, [id]);

  if (!b) return <div className="container-x py-20 text-[#A8B3AD]">Laden…</div>;

  const addAll = () => {
    (b.hardware || []).forEach((h) =>
      addToCart({ type: "hardware", ref_id: h.id, sku: h.sku, name: h.name,
        unit_price: h.purchase_price * (1 - (b.discount_pct || 0) / 100), monthly_price: 0, pricing_mode: "buy" }));
    (b.software || []).forEach((s) =>
      addToCart({ type: "software", ref_id: s.id, sku: s.slug, name: s.name,
        unit_price: s.one_time_setup || 0, monthly_price: s.price_monthly || 0, pricing_mode: "subscription" }));
    (b.addons || []).forEach((a) =>
      addToCart({ type: "addon", ref_id: a.id, sku: a.sku, name: a.name, unit_price: a.price, monthly_price: 0, pricing_mode: "buy" }));
    toast.success("Bundel toegevoegd aan mand");
    nav("/cart");
  };

  const once = (b.hardware || []).reduce((n, h) => n + h.purchase_price * (1 - (b.discount_pct || 0) / 100), 0)
    + (b.addons || []).reduce((n, a) => n + a.price, 0)
    + (b.software || []).reduce((n, s) => n + (s.one_time_setup || 0), 0);
  const monthly = (b.software || []).reduce((n, s) => n + (s.price_monthly || 0), 0);

  return (
    <div className="container-x py-12" data-testid="bundle-detail-page">
      <div className="grid md:grid-cols-2 gap-10 items-center">
        {b.image_url && (
          <div className="bg-gradient-to-br from-[#1C2724] to-[#131B18] rounded-2xl p-8 h-[60vh] grid place-items-center">
            <img src={b.image_url} alt={tr(b.name_i18n, lang)} className="max-h-full max-w-full object-contain" />
          </div>
        )}
        <div>
          <div className="chip chip-accent mb-3">{b.discount_pct}% voordeel · aanbevolen</div>
          <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{tr(b.name_i18n, lang)}</h1>
          <p className="mt-4 text-[#A8B3AD] leading-relaxed">{tr(b.description_i18n, lang)}</p>
          <div className="mt-6 card-surface p-5 space-y-4">
            <Section title="Hardware">
              {(b.hardware || []).map((h) => (
                <Row key={h.id} left={h.name} right={formatPrice(h.purchase_price)} />
              ))}
            </Section>
            <Section title="Software">
              {(b.software || []).map((s) => (
                <Row key={s.id} left={s.name} right={s.price_monthly ? `${formatPrice(s.price_monthly)}/mnd` : formatPrice(s.one_time_setup || 0)} />
              ))}
            </Section>
            <Section title="Add-ons">
              {(b.addons || []).map((a) => (
                <Row key={a.id} left={a.name} right={formatPrice(a.price)} />
              ))}
            </Section>
            <div className="pt-3 border-t border-[#243430] flex justify-between font-semibold">
              <span>Totaal eenmalig (incl. korting)</span><span className="text-[#6FAA8A]">{formatPrice(once)}</span>
            </div>
            <div className="flex justify-between text-sm text-[#A8B3AD]">
              <span>Per maand software</span><span>{formatPrice(monthly)}/mnd</span>
            </div>
          </div>
          <button className="btn-accent mt-6" onClick={addAll} data-testid="bundle-add-cart-button">
            <CheckCircle2 className="w-4 h-4" /> Bundel toevoegen aan mand
          </button>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A] mb-2">{title}</div>
    {children}
  </div>
);
const Row = ({ left, right }) => (
  <div className="flex justify-between text-sm py-1">
    <span className="text-[#A8B3AD]">{left}</span>
    <span className="text-[#F4F1EA] font-medium">{right}</span>
  </div>
);
